/**
 * CASE.SHE — «Доповни образ»: захист від повторного кліку.
 *
 * Саме додавання в кошик тут не робиться: його веде штатний Horizon
 * `product-form-component` у кожному рядку (`assets/product-form.js`), і він же
 * відправляє стандартну подію `shopify:cart:lines-update`, з якої оновлюються
 * лічильник кошика, cart drawer і решта сторінки.
 *
 * Модуль додає два правила, обидва лише для кнопок цього блока:
 *
 * - доки запит рядка ще не завершився, наступний клік по його кнопці не
 *   відправляє другий запит і не запускає вдруге анімацію «додано». Стан
 *   «зайнято» — атрибут `aria-busy` на формі рядка; він ставиться, коли форма
 *   відправляє подію кошика, і знімається, коли її проміс завершився успіхом чи
 *   помилкою. Страхувальний тайм-аут не лишає рядок заблокованим, якщо відповідь
 *   так і не прийде;
 * - якщо додати не вдалося, оптимістична «галочка» Horizon (`data-added`)
 *   знімається одразу, щоб поруч із текстом помилки не стояла позначка успіху.
 *
 * Слухачі висять на самому блоці, тож основна форма товару, sticky Add to cart
 * і інші секції їх не бачать.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const BUSY_ATTRIBUTE = 'aria-busy';
const BUSY_TIMEOUT_MS = 15000;

class CaseSheUpsell extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Capture: рішення приймається до делегованих обробників Horizon на document.
    this.addEventListener('click', this.#handleClick, { capture: true, signal });
    this.addEventListener(CartLinesUpdateEvent.eventName, this.#handleCartLinesUpdate, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('button[name="add"]');
    if (!button || !this.contains(button)) return;

    const form = button.closest('product-form-component');
    if (form?.getAttribute(BUSY_ATTRIBUTE) !== 'true') return;

    event.preventDefault();
    event.stopPropagation();
  };

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #handleCartLinesUpdate = (event) => {
    if (!(event.target instanceof Element)) return;

    const form = event.target.closest('product-form-component');
    if (!form || !this.contains(form)) return;

    form.setAttribute(BUSY_ATTRIBUTE, 'true');

    const timeoutId = window.setTimeout(release, BUSY_TIMEOUT_MS);
    function release() {
      window.clearTimeout(timeoutId);
      form?.removeAttribute(BUSY_ATTRIBUTE);
    }
    function clearAddedState() {
      form?.querySelector('[ref="addToCartButton"]')?.removeAttribute('data-added');
    }

    Promise.resolve(event.promise).then(
      (result) => {
        if (result?.detail?.didError) clearAddedState();
        release();
      },
      () => {
        clearAddedState();
        release();
      }
    );
  };
}

if (!customElements.get('case-she-upsell')) {
  customElements.define('case-she-upsell', CaseSheUpsell);
}
